import { formatRegion } from '@platform/design-system';
import type { CatalogOption, OptionValue } from './options.ts';

const ALPHABET_LENGTH = 26;

const LETTERS = Array.from({ length: ALPHABET_LENGTH }, (_, offset) =>
  String.fromCharCode('A'.charCodeAt(0) + offset),
);

const GROUPINGS = new Set(['EU', 'EZ', 'UN', 'QO', 'XA', 'XB', 'ZZ']);

const knownRegions = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' });

const isCurrentCode = (code: string): boolean => Intl.getCanonicalLocales(`und-${code}`)[0] === `und-${code}`;

export const COUNTRY_CODES: readonly string[] = LETTERS.flatMap((first) =>
  LETTERS.map((second) => `${first}${second}`),
).filter((code) => !GROUPINGS.has(code) && knownRegions.of(code) !== undefined && isCurrentCode(code));

export type CountryChoice = { readonly value: string; readonly label: string };

export const countryChoices = (locale: string, excluded: ReadonlySet<string>): readonly CountryChoice[] => {
  const collator = new Intl.Collator(locale);
  return COUNTRY_CODES.filter((code) => !excluded.has(code))
    .map((code) => ({ value: code, label: formatRegion(code, locale) }))
    .toSorted((left, right) => collator.compare(left.label, right.label));
};

export const originOptionOf = (options: readonly CatalogOption[]): CatalogOption | undefined =>
  options.find((option) => option.kind === 'origin');

export const ordinaryOptionsOf = (options: readonly CatalogOption[]): readonly CatalogOption[] =>
  options.filter((option) => option.kind !== 'origin');

export type OriginLabels = { readonly local: string; readonly imported: (country: string) => string };

export const originLabelOf = (
  value: Pick<OptionValue, 'countryCode'>,
  labels: OriginLabels,
  locale: string,
): string =>
  value.countryCode === null ? labels.local : labels.imported(formatRegion(value.countryCode, locale));
